import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import ReactModal from 'react-modal';

import { GroupDetailInterface, UserGroupInterface } from 'types/user';
import { ModalContext } from 'context/ModalContext';
import Box from 'components/ui/box';
import { useAuth } from 'context/AuthContext';
import { getUserGroup } from 'services/dashboard.service';

import { IoIosClose } from 'react-icons/io';
import Button from 'components/ui/button';

type Props = {
  group: {
    nome: string | undefined;
    total: number | undefined;
    grupo_definido?: boolean;
  };
};

const useFocusTrap = (ref: React.RefObject<HTMLDivElement>, isOpen: boolean, onClose: () => void) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !ref.current) return;
      event.preventDefault();

      const focusableElements = ref.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusableElements || focusableElements.length === 0) return;

      const focusableArray = Array.from(focusableElements) as HTMLElement[];
      const currentIndex = focusableArray.indexOf(document.activeElement as HTMLElement);

      let nextIndex: number;
      if (event.shiftKey) {
        nextIndex = currentIndex <= 0 ? focusableArray.length - 1 : currentIndex - 1;
      } else {
        nextIndex = currentIndex >= focusableArray.length - 1 ? 0 : currentIndex + 1;
      }

      focusableArray[nextIndex]?.focus();
    };

    const timeoutId = setTimeout(() => {
      if (ref.current) {
        const firstFocusable = ref.current.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) as HTMLElement;
        firstFocusable?.focus();
      }
    }, 50);

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, ref, onClose]);
};

export function GroupDetailModal({ group }: Props) {
  const { user } = useAuth();
  const { openModal, closeModal } = useContext(ModalContext);

  const modalID = 'group-detail';

  const [modalIsOpen, setIsOpen] = useState<boolean>(false);
  const [groupDetail, setGroupDetail] = useState<GroupDetailInterface[]>();
  const modalContentRef = useRef<HTMLDivElement>(null);

  ReactModal.setAppElement('#modal');

  function handleCloseModal() {
    //
  }

  function afterOpenModal() {
    //
  }

  function mountGroupDetail(groups: UserGroupInterface[]) {
    const table: any = [];

    if (groups?.length) {
      groups.forEach((group: UserGroupInterface, index) => {
        const prefixoTable = table.find((x: any) => x.prefixo === group.prefixo);

        if (prefixoTable) {
          const ro = prefixoTable.ROs.find((x: any) => x.ro === group.ro);

          if (ro) {
            if (!ro.funcoes.find((x: any) => x.cod_funcao === group.cod_funcao)) {
              ro.funcoes.push({
                funcao: group.funcao,
                cod_funcao: group.cod_funcao,
              });
            }
          } else {
            prefixoTable.ROs.push({
              ro: group.ro,
              funcoes: [
                {
                  funcao: group.funcao,
                  cod_funcao: group.cod_funcao,
                },
              ],
            });
          }
        } else {
          table.push({
            nome_dependencia: group.nome_dependencia,
            prefixo: group.prefixo,
            ROs: [
              {
                ro: group.ro,
                funcoes: [
                  {
                    funcao: group.funcao,
                    cod_funcao: group.cod_funcao,
                  },
                ],
              },
            ],
          });
        }
      });

      setGroupDetail(table);
    }
  }

  useEffect(() => {
    setIsOpen(openModal === modalID);
  }, [openModal]);

  const handleFocusTrapClose = useCallback(() => {
    closeModal();
  }, [closeModal]);

  useFocusTrap(modalContentRef, modalIsOpen, handleFocusTrapClose);

  useEffect(() => {
    async function loadUserGroup() {
      if (user?._id) {
        const data: UserGroupInterface[] = await getUserGroup(user._id);
        mountGroupDetail(data);
      }
    }

    if (!user || !modalIsOpen) return;

    loadUserGroup();
  }, [user, modalIsOpen]);

  if (!groupDetail?.length) return <></>;

  return (
    <>
      <ReactModal
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={true}
        isOpen={modalIsOpen}
        onAfterOpen={afterOpenModal}
        onRequestClose={handleCloseModal}
        contentLabel="Example Modal"
        portalClassName="relative z-50"
        overlayClassName="fixed z-100 top-0 left-0 bg-black bg-opacity-80 flex flex-col items-center   w-screen h-screen p-6 pt-[10vh] overflow-y-auto"
        contentElement={() => (
          <div ref={modalContentRef} role="dialog" aria-modal="true" aria-labelledby="group-detail-modal-title">
            <Box className="w-[90%] lg:w-auto lg:max-w-[800px] ">
              <Button
                color="primary"
                className="absolute -right-5 -top-5  rounded-r-full rounded-l-full w-10 h-10 p-0 flex justify-center items-center"
                onClick={() => closeModal()}
                aria-label="Fechar modal"
              >
                <IoIosClose color="white" size={40} aria-hidden="true" />
              </Button>

              <div className="flex justify-center text-xl text-yellow">
                <h1 tabIndex={0} id="group-detail-modal-title">Grupo {group?.nome}</h1>
              </div>

              <div className="overflow-x-auto">
                <div className="border-electric-blue border rounded-xl w-[731px] ">
                  <div className="flex font-bold">
                    <div tabIndex={0} className="w-36 flex items-center p-2 pl-5">Função</div>
                    <div tabIndex={0} className="w-48 border-electric-blue border-l flex items-center p-2 pl-5">Nome da função</div>
                    <div tabIndex={0} className="w-20 border-electric-blue border-l flex items-center p-2 pl-5">RO</div>
                    <div tabIndex={0} className="w-24 border-electric-blue border-l flex items-center p-2 pl-5">Prefixo</div>
                    <div tabIndex={0} className="w-48 border-electric-blue border-l flex items-center p-2 pl-5">Nome da dependência</div>
                  </div>
                  {groupDetail.map((group, index) => (
                    <div
                      className={`pr-[26px] w-full flex flex-row-reverse border-electric-blue border-t font-thin`}
                      key={group.nome_dependencia}
                    >
                      <div tabIndex={0} className="nome-dependencia border-electric-blue border-l flex items-center justify-center w-48 p-2">
                        {group.nome_dependencia}{' '}
                      </div>
                      <div tabIndex={0} className="prefixo border-electric-blue border-l flex justify-center items-center w-24 p-2">
                        {group.prefixo}
                      </div>
                      <div className="ros flex flex-col">
                        {group.ROs.map(ro => (
                          <div className="ro flex flex-row-reverse h-full" key={ro.ro}>
                            <div tabIndex={0} className="sigla border-electric-blue border-l flex justify-center items-center w-20 p-2">
                              {ro.ro}
                            </div>
                            <div className="flex flex-col h-full">
                              {ro.funcoes.map((funcao, ind) => (
                                <div
                                  className={`flex h-full border-electric-blue  ${ind > 0 && 'border-t'}`}
                                  key={funcao.cod_funcao}
                                >
                                  <div tabIndex={0} className="w-36 flex justify-center items-center p-2">{funcao.cod_funcao}</div>
                                  <div tabIndex={0} className="border-electric-blue border-l w-48 justify-center items-center p-2">
                                    {funcao.funcao}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <p tabIndex={0} className="mt-5 font-thin">
                Total de funcionários do grupo: <span className="text-yellow">{group?.total} pessoas.</span>
              </p>

              <p tabIndex={0} className="mt-3 font-thin">
                {!!group.grupo_definido ? (
                  <>
                    O seu grupo está <span className="text-yellow">definido</span>.
                  </>
                ) : (
                  <>
                    O seu grupo <span className="text-yellow">poderá ser alterado</span> até o fechamento do resultado do ciclo do
                    Programa.
                  </>
                )}
              </p>
            </Box>
          </div>
        )}
      />
    </>
  );
}
